import './mini-listing-preview.css';

import React, { useEffect, useMemo, useRef } from 'react';
import { OverlayView } from '@react-google-maps/api';
import { isNullOrUndefinedOrBlank } from '../../util/isNullOrUndefinedOrBlank';
import { IListing } from '../../models/listing.model';
import { convertToInternationalCurrencySystem } from '../../util/format-number';
import ListingLink from '../../listing-link/listing-link';
import { ICoord } from '../../models/coord.model';
import { listDateToTimeOnMarket } from '../../util/list-date-to-time-on-market';
import { iconBed, iconBath, iconParking, iconPropertySize, iconTimeOnMarket } from '../../util/icons';

interface MiniListingPreviewHighlightProps {
  value: string | number;
  icon: React.ReactNode;
}

const MiniListingPreviewHighlight = (props: MiniListingPreviewHighlightProps) => {
  return (
    <div>
      <div>{props.icon}</div>
      <div>{props.value}</div>
    </div>
  );
};

export interface MiniListingPreviewProps {
  listing: IListing;
  getOverlayPixelPositionOffset(width: number, height: number, coord: ICoord | undefined): { x: number; y: number; };
  setActivePreviewListingId(value: string): void;
  onMouseOver(listingId: string): void;
  onMouseOut(): void;
}

export const MiniListingPreview: React.FC<MiniListingPreviewProps> = props => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const timeOnMarket = useMemo(
    () => listDateToTimeOnMarket(props.listing.listDate),
    [props.listing.listDate]
  );
  const highlights: MiniListingPreviewHighlightProps[] = useMemo(
    () => ([
      { icon: iconBed, value: props.listing.totalBeds },
      { icon: iconBath, value: props.listing.totalBaths },
      { icon: iconParking, value: props.listing.parkingSpaces },
      { icon: iconPropertySize, value: props.listing.propertySize }
    ]),
    [props.listing.parkingSpaces, props.listing.propertySize, props.listing.totalBaths, props.listing.totalBeds]
  );

  const highlightsRender = useMemo(() =>
    highlights.map((h, i) => !isNullOrUndefinedOrBlank(h.value) && h.value !== 0 &&
      <MiniListingPreviewHighlight key={i} icon={h.icon} value={h.value} />
    ), [highlights]);

  const timeOnMarketRender = useMemo(() =>
    <MiniListingPreviewHighlight icon={iconTimeOnMarket} value={timeOnMarket.value} />
    , [timeOnMarket.value]
  );


  const handleClickOutside = (event: any) => {
    if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
      props.setActivePreviewListingId('');
    }
  };

  const getOverlayPixelPositionOffset = (width: number, height: number) => {
    return props.getOverlayPixelPositionOffset(width, height, props.listing?.coords);
  };

  useEffect(() => {
    document.addEventListener("click", handleClickOutside)
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  });

  if (props.listing === null || isNullOrUndefinedOrBlank(props.listing.coords.lat) || isNullOrUndefinedOrBlank(props.listing.coords.lng)) {
    return <></>
  }

  return (
    <OverlayView
      position={{
        lat: props.listing.coords.lat,
        lng: props.listing.coords.lng
      }}
      mapPaneName={OverlayView.FLOAT_PANE}
      getPixelPositionOffset={getOverlayPixelPositionOffset}
    >
      <div ref={ref => (containerRef.current = ref)} className="mini-listing-preview" onMouseEnter={() => props.onMouseOver(props.listing.id)} onMouseLeave={props.onMouseOut}>
        {props.listing.listingPhotos.length > 0 && props.listing.listingPhotos[0].url !== "" &&
          <div className="mini-listing-preview-picture">
            <ListingLink listing={props.listing}>
              <img loading="lazy" src={props.listing.listingPhotos[0].url} alt={props.listing.listingPhotos[0].description} />
            </ListingLink>
          </div>
        }
        <ListingLink listing={props.listing}>
          <div className="mini-listing-preview-data">
            <div className="mini-listing-preview-data-price">
              ${convertToInternationalCurrencySystem(props.listing.price)}
            </div>
            <div className="mini-listing-preview-highlights">
              {highlightsRender}
              {timeOnMarketRender}
            </div>
          </div>
        </ListingLink>
      </div>
    </OverlayView>
  );
}

export default MiniListingPreview;
